import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { AuthService } from '@app-shared/services/auth/auth.service';
import { NotFoundException } from '@app-shared/services/not-found-exception';
import { PersonService, PersonStatus } from '@app-shared/services/person/person.service';

/**
 * Functional guard for the onboarding routes.
 *
 * Determines if a user can access the onboarding flow:
 * - If user is authenticated and has completed onboarding, redirects to dashboard
 * - If user is in onboarding state, allows access
 * - If user is anonymous, allows access
 * - If no user exists, creates an anonymous session and allows access
 *
 * The flow now starts at the goal screen (ad traffic lands there directly), so
 * every onboarding route gets this bootstrap on entry. Entity creation and
 * position restore follow in OnboardingDataResolver.
 */
export const onboardingGuard: CanActivateFn = async (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const personService = inject(PersonService);

  const currentUser = await firstValueFrom(authService.getFirebaseUser$().pipe(take(1)));

  const userHasBeenIdentified = currentUser !== null && !currentUser.isAnonymous;
  const userExistsAnonymously = currentUser !== null && currentUser.isAnonymous;

  // Check to see if user has visited previously and
  // log them out if they haven't visited in a long time
  if (currentUser !== null) {
    const expireDays = 30;
    const lastSignInDate = new Date(currentUser.metadata.lastSignInTime!);
    lastSignInDate.setDate(lastSignInDate.getDate() + expireDays);
    if (new Date() > lastSignInDate) {
      console.log('User session expired, logging out');
      await router.navigate(['/logout'], { queryParams: { expired: 1 } });
      return false;
    }
  }

  if (userHasBeenIdentified) {
    // We need to check if this user exists already
    try {
      const person = await personService.getOne(currentUser.uid);

      // Check to see if the user is still onboarding
      if (
        person.person_status === PersonStatus.anonymous ||
        person.person_status === PersonStatus.browsing
      ) {
        return true;
      }
    } catch (e) {
      // If an account was NOT found then they can see the onboarding
      if (e instanceof NotFoundException) {
        return true; // Let them through, the resolver will handle it
      } else {
        throw e;
      }
    }

    // Else they have a usable account - take them to the dashboard
    await router.navigate(['./dashboard']);
    return false;
  }

  if (userExistsAnonymously) {
    return true;
  }

  // No user exists, create an anonymous session. Wait for its ID token before
  // letting the resolver write the initial entities, so the first Firestore
  // write never races token propagation (the H5 race).
  const credential = await authService.signInAnonymously();
  await credential.user.getIdToken();
  return true;
};

/**
 * @deprecated Use the functional `onboardingGuard` instead.
 * This class is kept for backward compatibility during migration.
 */
export { onboardingGuard as OnboardingGuard };
